import { patchState, signalStore, withMethods, withState } from '@ngrx/signals'

/** Router commands to the designer page of the resource that was run, with the project they belong to. */
export type RunDesignerLink = {
  appId: string
  commands: readonly string[]
}

type RunDesignerLinkState = {
  link: RunDesignerLink | null
}

/**
 * Where the Config tab leads after a run: the designer page of the resource that was being run.
 *
 * A run page leaves the moment the maker picks Runtime, so it cannot be asked afterwards. It
 * leaves the link here as resolved commands (not a closure over the route, which has changed by
 * then), and the banner takes it the next time Config is picked. Provided by the layout, so it
 * lives as long as the project does.
 */
export const RunDesignerLinkStore = signalStore(
  withState<RunDesignerLinkState>({ link: null }),
  withMethods((store) => ({
    /** Remember the designer page of the resource being run. A later run replaces it. */
    remember: (link: RunDesignerLink): void => patchState(store, { link }),

    /** The link for this project, which is forgotten as it is handed over. A link left by another
     * project's run is not this one's to follow. */
    take: (appId: string): readonly string[] | null => {
      const link = store.link()

      if (!link || link.appId !== appId) {
        return null
      }

      patchState(store, { link: null })

      return link.commands
    },

    forget: (): void => patchState(store, { link: null }),
  }))
)
